import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { X, ZoomIn, ZoomOut, RotateCw, Check } from 'lucide-react';

const getCroppedImg = (imageSrc, pixelCrop, rotation = 0) => {
  return new Promise((resolve) => {
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      // Fix Sep 2026 — appliquer la rotation choisie dans le Cropper au rendu
      // final (react-easy-crop fournit pixelCrop relatif à l'image tournée).
      const radians = (rotation * Math.PI) / 180;
      const sin = Math.abs(Math.sin(radians));
      const cos = Math.abs(Math.cos(radians));
      const bBoxWidth = Math.round(image.width * cos + image.height * sin);
      const bBoxHeight = Math.round(image.width * sin + image.height * cos);
      const rotCanvas = document.createElement('canvas');
      rotCanvas.width = bBoxWidth;
      rotCanvas.height = bBoxHeight;
      const rctx = rotCanvas.getContext('2d');
      rctx.translate(bBoxWidth / 2, bBoxHeight / 2);
      rctx.rotate(radians);
      rctx.drawImage(image, -image.width / 2, -image.height / 2);
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        rotCanvas,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    };
    image.src = imageSrc;
  });
};

const ImageCropModal = ({ isOpen, onClose, imageSrc, aspect, aspectOptions, onCropComplete, title }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  // Brief LOT4.1 — ratio ajustable quand le contexte le permet (posts, etc.)
  const [activeAspect, setActiveAspect] = useState(aspect || 1);
  React.useEffect(() => { setActiveAspect(aspect || 1); }, [aspect, imageSrc]);

  const onCropChange = useCallback((_, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
    const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
    onCropComplete(file);
    onClose();
  };

  if (!isOpen || !imageSrc) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" data-testid="image-crop-modal">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">{title || 'Recadrer l\'image'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center">
            <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="relative h-80 bg-gray-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={activeAspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropChange}
            cropShape={!aspectOptions && aspect === 1 ? 'round' : 'rect'}
            showGrid={true}
          />
        </div>

        <div className="p-4 space-y-3">
          {/* Brief LOT4.1 — choix du cadrage (évite le zoom forcé sur un seul ratio) */}
          {Array.isArray(aspectOptions) && aspectOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="crop-aspect-options">
              {aspectOptions.map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setActiveAspect(opt.value)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    Math.abs(activeAspect - opt.value) < 0.01
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-purple-300'
                  }`}
                  data-testid={`crop-aspect-${opt.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3">
            <ZoomOut className="h-4 w-4 text-gray-400" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-purple-600"
              data-testid="crop-zoom-slider"
            />
            <ZoomIn className="h-4 w-4 text-gray-400" />
            <button
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
              title="Rotation"
            >
              <RotateCw className="h-4 w-4" />
            </button>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button onClick={handleConfirm} className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600" data-testid="crop-confirm-btn">
              <Check className="h-4 w-4 mr-2" />Appliquer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
